/**
 * Off-table coach tools on a training exercise (planning → live training).
 * Mirrors Backend/src/utils/teCoachTools.pure.js
 */

export const COACH_TOOL_CHRONO = "chrono";
export const COACH_TOOL_COUNT = "count";

function blankTools() {
  return {
    chrono: false,
    count: false,
    count_label: "",
    timer_only: false,
  };
}

/**
 * @param {unknown} raw
 * @param {{ defaultChrono?: boolean }} [opts]
 */
export function parseTeCoachTools(raw, opts = {}) {
  const defaultChrono = opts.defaultChrono !== false;
  const base = blankTools();
  if (defaultChrono) base.chrono = true;

  if (raw == null || raw === "") return base;

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return base;
    }
  }

  if (Array.isArray(parsed)) {
    const tools = blankTools();
    for (const item of parsed) {
      const s = String(item || "").trim().toLowerCase();
      if (s === COACH_TOOL_CHRONO || s === "timer" || s === "chronometer") {
        tools.chrono = true;
      }
      if (s === COACH_TOOL_COUNT || s === "reps" || s === "counter") {
        tools.count = true;
      }
      if (s.startsWith("count:")) {
        tools.count = true;
        tools.count_label = s.slice(6).trim();
      }
    }
    return tools;
  }

  if (parsed && typeof parsed === "object") {
    const tools = blankTools();
    if (Object.prototype.hasOwnProperty.call(parsed, "chrono")) {
      tools.chrono = Boolean(parsed.chrono);
    } else if (defaultChrono) {
      tools.chrono = true;
    }
    if (Object.prototype.hasOwnProperty.call(parsed, "count")) {
      tools.count = Boolean(parsed.count);
    }
    if (Object.prototype.hasOwnProperty.call(parsed, "timer_only")) {
      tools.timer_only = Boolean(parsed.timer_only);
    } else if (Object.prototype.hasOwnProperty.call(parsed, "timerOnly")) {
      tools.timer_only = Boolean(parsed.timerOnly);
    }
    const label = parsed.count_label ?? parsed.countLabel ?? "";
    tools.count_label = String(label || "").trim().slice(0, 80);
    return tools;
  }

  return base;
}

export function serializeTeCoachTools(input) {
  const tools = parseTeCoachTools(input, { defaultChrono: false });
  if (input && typeof input === "object" && !Array.isArray(input)) {
    if (Object.prototype.hasOwnProperty.call(input, "chrono")) {
      tools.chrono = Boolean(input.chrono);
    }
    if (Object.prototype.hasOwnProperty.call(input, "count")) {
      tools.count = Boolean(input.count);
    }
    if (
      Object.prototype.hasOwnProperty.call(input, "count_label") ||
      Object.prototype.hasOwnProperty.call(input, "countLabel")
    ) {
      tools.count_label = String(
        input.count_label ?? input.countLabel ?? ""
      )
        .trim()
        .slice(0, 80);
    }
    if (
      Object.prototype.hasOwnProperty.call(input, "timer_only") ||
      Object.prototype.hasOwnProperty.call(input, "timerOnly")
    ) {
      tools.timer_only = Boolean(input.timer_only ?? input.timerOnly);
    }
  }
  const out = {
    chrono: Boolean(tools.chrono),
    count: Boolean(tools.count),
    count_label: tools.count_label || "",
  };
  if (tools.timer_only) out.timer_only = true;
  return JSON.stringify(out);
}

export function coachToolsFromExercise(ex, opts = {}) {
  if (!ex) return parseTeCoachTools(null, opts);
  return parseTeCoachTools(
    ex.te_coach_tools ?? ex.coach_tools ?? null,
    opts
  );
}

/** Empty Live Training timer block (no catalog exercise, chrono-only). */
export function isLiveTimerBlock(ex) {
  if (!ex?.te_id) return false;
  if (ex.ex_id != null && String(ex.ex_id).trim()) return false;
  const tools = coachToolsFromExercise(ex, { defaultChrono: false });
  return tools.chrono === true && tools.timer_only === true;
}
